import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Typography,
  Box,
  IconButton,
  Chip,
  InputAdornment,
  Alert,
} from "@mui/material";
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  Star as StarIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { grey, red } from "@mui/material/colors";

const RoomEditModal = ({ open, room, onClose, onSave, loading = false }) => {
  const [formData, setFormData] = useState({
    name: "",
    type: "single",
    status: "private",
    is_active: true,
    join_fee: "",
    watch_fee: "",
    duration_in_mins: "",
    user_stream_time_in_mins: "",
    user_xp_level: "",
    team_limit: "",
    total_credits: "",
    tags: "",
    expiration_time: "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (room && open) {
      setFormData({
        name: room.name || "",
        type: room.type || "single",
        status: room.status || "private",
        is_active: room.is_active !== undefined ? room.is_active : true,
        join_fee:
          room.join_fee !== null && room.join_fee !== undefined
            ? room.join_fee
            : "",
        watch_fee:
          room.watch_fee !== null && room.watch_fee !== undefined
            ? room.watch_fee
            : "",
        duration_in_mins: room.duration_in_mins || "",
        user_stream_time_in_mins: room.user_stream_time_in_mins || "",
        user_xp_level:
          room.user_xp_level !== null && room.user_xp_level !== undefined
            ? room.user_xp_level
            : "",
        team_limit: room.team_limit || "",
        total_credits:
          room.total_credits !== null && room.total_credits !== undefined
            ? room.total_credits
            : "",
        tags: room.tags || "",
        expiration_time: room.expiration_time
          ? room.expiration_time.split("T")[0]
          : "", // Format for date input
      });
      setErrors({});
    }
  }, [room, open]);

  const handleChange = (field) => (event) => {
    const value =
      event.target.type === "checkbox"
        ? event.target.checked
        : event.target.value;

    // Special handling for is_active field with expired room validation
    if (field === "is_active" && value === true && room?.is_expired) {
      // Don't allow activation of expired rooms
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Room name is required";
    }

    if (formData.name.trim().length > 255) {
      newErrors.name = "Room name cannot exceed 255 characters";
    }

    // Validate numeric fields according to backend schema
    const numericFields = ["join_fee", "watch_fee", "user_xp_level"];
    numericFields.forEach((field) => {
      if (
        formData[field] &&
        (isNaN(formData[field]) || Number(formData[field]) < 0)
      ) {
        newErrors[field] = "Must be a valid positive number";
      }
    });

    // String fields with max length constraints
    const stringFields = [
      "duration_in_mins",
      "user_stream_time_in_mins",
      "team_limit",
    ];
    stringFields.forEach((field) => {
      if (formData[field] && formData[field].length > 10) {
        newErrors[field] = "Maximum 10 characters allowed";
      }
    });

    // Tags validation
    if (formData.tags && formData.tags.length > 500) {
      newErrors.tags = "Tags cannot exceed 500 characters";
    }

    // Expiration time validation
    if (formData.expiration_time) {
      const expDate = new Date(formData.expiration_time);
      const now = new Date();
      if (expDate <= now) {
        newErrors.expiration_time = "Expiration time must be in the future";
      }
    }

    // Business rule validation: Cannot activate expired rooms
    if (formData.is_active && room?.is_expired) {
      newErrors.is_active = "Cannot activate expired room";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    // Fields allowed by UpdateRoomSchema in backend
    const allowedFields = [
      "name",
      "status",
      "join_fee",
      "watch_fee",
      "duration_in_mins",
      "user_stream_time_in_mins",
      "user_xp_level",
      "team_limit",
      "tags",
      "expiration_time",
    ];

    // Prepare data for API according to backend UpdateRoomSchema
    const updateData = {};

    // Only include allowed fields that have values
    allowedFields.forEach((field) => {
      if (formData[field] !== undefined && formData[field] !== "") {
        updateData[field] = formData[field];
      }
    });

    // Remove empty string values and convert to appropriate types
    Object.keys(updateData).forEach((key) => {
      if (updateData[key] === "" || updateData[key] === null) {
        delete updateData[key];
      }
    });

    // Convert numeric fields to integers
    const numericFields = ["join_fee", "watch_fee", "user_xp_level"];
    numericFields.forEach((field) => {
      if (updateData[field] !== undefined) {
        updateData[field] = parseInt(updateData[field], 10);
      }
    });

    // Clean up string fields
    const stringFields = ["name", "tags"];
    stringFields.forEach((field) => {
      if (updateData[field] !== undefined) {
        updateData[field] = updateData[field].trim();
      }
    });

    // Handle expiration_time - let backend handle the formatting
    if (updateData.expiration_time) {
      // Just send the date value, backend will handle formatting
      updateData.expiration_time = formData.expiration_time;
    }

    // Additional restriction: Remove restricted fields for direct messages
    if (room?.type === "single") {
      const restrictedFields = ["name", "join_fee", "watch_fee"];
      restrictedFields.forEach((field) => {
        delete updateData[field];
      });
    }

    onSave(updateData);
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  if (!room) return null;

  const isDirectMessage = room.type === "single";
  const canEditType = !isDirectMessage; // Only allow type changes for group rooms
  const isExpired = room.is_expired;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
        },
      }}
    >
      <DialogTitle sx={{ pb: 2, px: 3, pt: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
              Edit Room
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Update room settings and configuration
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{
              bgcolor: grey[50],
              border: `1px solid ${grey[200]}`,
              "&:hover": {
                bgcolor: grey[100],
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 2 }}>
        <Grid container spacing={3} sx={{ mt: 0.5 }}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Basic Information
            </Typography>
            {isDirectMessage && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Direct Message Room:</strong> Some settings are
                  limited for direct message rooms.
                </Typography>
              </Alert>
            )}
            {isExpired && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Expired Room:</strong> This room has expired and
                  cannot be activated. Expired on:{" "}
                  {room.expiration_time
                    ? new Date(room.expiration_time).toLocaleDateString()
                    : "Unknown"}
                </Typography>
              </Alert>
            )}
          </Grid>

          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              label="Room Name"
              value={formData.name}
              onChange={handleChange("name")}
              error={!!errors.name}
              helperText={
                errors.name ||
                (isDirectMessage ? "Limited editing for direct messages" : "")
              }
              required
              disabled={isDirectMessage} // Disable name editing for direct messages
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Room Type</InputLabel>
              <Select
                value={formData.type}
                label="Room Type"
                onChange={handleChange("type")}
                disabled={true} // Always disabled - room type cannot be changed after creation
              >
                <MenuItem value="single">Direct Message</MenuItem>
                <MenuItem value="group">Group Chat</MenuItem>
              </Select>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                Room type cannot be changed after creation
              </Typography>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Visibility</InputLabel>
              <Select
                value={formData.status}
                label="Visibility"
                onChange={handleChange("status")}
              >
                <MenuItem value="private">Private</MenuItem>
                <MenuItem value="public">Public</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={handleChange("is_active")}
                  color="primary"
                  disabled={isExpired} // Disable if room is expired
                />
              }
              label={
                <Box>
                  <Typography variant="body2">Room is Active</Typography>
                  {isExpired && (
                    <Typography variant="caption" color="error">
                      Cannot activate expired room
                    </Typography>
                  )}
                </Box>
              }
            />
            {errors.is_active && (
              <Typography
                variant="caption"
                color="error"
                sx={{ display: "block", mt: 1 }}
              >
                {errors.is_active}
              </Typography>
            )}
          </Grid>

          {/* Financial Settings - Limited for direct messages */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2, mt: 2, fontWeight: 600 }}>
              Financial Settings
              {isDirectMessage && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 1 }}
                >
                  (Limited for direct messages)
                </Typography>
              )}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Join Fee"
              type="number"
              value={formData.join_fee}
              onChange={handleChange("join_fee")}
              error={!!errors.join_fee}
              helperText={errors.join_fee}
              disabled={isDirectMessage}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MoneyIcon color="action" />
                  </InputAdornment>
                ),
                inputProps: { min: 0 },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Watch Fee"
              type="number"
              value={formData.watch_fee}
              onChange={handleChange("watch_fee")}
              error={!!errors.watch_fee}
              helperText={errors.watch_fee}
              disabled={isDirectMessage}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MoneyIcon color="action" />
                  </InputAdornment>
                ),
                inputProps: { min: 0 },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Total Credits"
              type="number"
              value={formData.total_credits}
              onChange={handleChange("total_credits")}
              error={!!errors.total_credits}
              helperText={errors.total_credits}
              disabled={isDirectMessage}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MoneyIcon color="action" />
                  </InputAdornment>
                ),
                inputProps: { min: 0 },
              }}
            />
          </Grid>

          {/* Room Settings */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2, mt: 2, fontWeight: 600 }}>
              Room Settings
            </Typography>
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Duration (minutes)"
              value={formData.duration_in_mins}
              onChange={handleChange("duration_in_mins")}
              error={!!errors.duration_in_mins}
              helperText={
                errors.duration_in_mins || "Leave empty for permanent room"
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <ScheduleIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Stream Time (minutes)"
              value={formData.user_stream_time_in_mins}
              onChange={handleChange("user_stream_time_in_mins")}
              error={!!errors.user_stream_time_in_mins}
              helperText={errors.user_stream_time_in_mins}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <ScheduleIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Required XP Level"
              type="number"
              value={formData.user_xp_level}
              onChange={handleChange("user_xp_level")}
              error={!!errors.user_xp_level}
              helperText={errors.user_xp_level}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <StarIcon color="action" />
                  </InputAdornment>
                ),
                inputProps: { min: 0 },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Team Limit"
              value={formData.team_limit}
              onChange={handleChange("team_limit")}
              error={!!errors.team_limit}
              helperText={errors.team_limit || "For group rooms only"}
              disabled={formData.type !== "group"}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Expiration Date"
              type="date"
              value={formData.expiration_time}
              onChange={handleChange("expiration_time")}
              error={!!errors.expiration_time}
              helperText={
                errors.expiration_time || "Leave empty for permanent room"
              }
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          {/* Tags */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Tags"
              value={formData.tags}
              onChange={handleChange("tags")}
              error={!!errors.tags}
              helperText={errors.tags || "Separate multiple tags with commas"}
              placeholder="gaming, social, competitive"
            />
            {formData.tags && (
              <Box sx={{ mt: 1, display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                {formData.tags
                  .split(",")
                  .filter((tag) => tag.trim())
                  .map((tag, index) => (
                    <Chip
                      key={index}
                      label={tag.trim()}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: "0.7rem" }}
                    />
                  ))}
              </Box>
            )}
          </Grid>

          {/* Current Status Display */}
          <Grid item xs={12}>
            <Alert severity={isExpired ? "error" : "info"} sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Current Status:</strong> This{" "}
                {isDirectMessage ? "direct message" : "group chat"} room has{" "}
                {room.participants?.length || 0} participants and was created on{" "}
                {room.created_at
                  ? new Date(room.created_at).toLocaleDateString()
                  : "unknown date"}
                .
                {isExpired && (
                  <>
                    <br />
                    <strong>⚠️ Room Expired:</strong> This room expired on{" "}
                    {room.expiration_time
                      ? new Date(room.expiration_time).toLocaleDateString()
                      : "unknown date"}
                    . Expired rooms cannot be activated.
                  </>
                )}
                {isDirectMessage && (
                  <>
                    <br />
                    <strong>Note:</strong> Direct message rooms have limited
                    editing capabilities for privacy and security.
                  </>
                )}
              </Typography>
            </Alert>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2, gap: 1 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          startIcon={<CancelIcon />}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={loading}
          sx={{
            background: "linear-gradient(45deg, #1976d2, #42a5f5)",
            "&:hover": {
              background: "linear-gradient(45deg, #1565c0, #1976d2)",
            },
          }}
        >
          {loading ? "Saving..." : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RoomEditModal;
